import Chart from "chart.js";
import clsx from "clsx";
import React, { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import Preloader from "~/components/Preloader";

import {
  BAR_CHART_TYPES,
  getChartData,
  getChartOptions,
  getStaticNumberBars,
} from "./options";

import s from "./Chart.less";

interface IProps {
  values?: number[];
  severalChartsValues?;
  labels?: Array<Array<string> | string>;
  type?: string;
  title?: string;
  loading?: boolean;
  customClassName?: string;
}

const DealerChart: FC<IProps> = (props) => {
  const { customClassName, type, loading = false } = props;

  const { ssr, after_sales, new_vehicle, used_vehicle, quality } =
    BAR_CHART_TYPES;
  return (
    <div
      className={clsx(customClassName, {
        [s.chart_container_2bars]: type === ssr,
        [s.chart_container_3bars]: type === after_sales,
        [s.chart_container_more3bars]:
          type === new_vehicle || type === used_vehicle,
        [s.chart_container_radar]: type === quality,
      })}
    >
      {loading ? (
        <Preloader />
      ) : type === quality ? (
        <ChartRadar {...props} />
      ) : (
        <ChartBar {...props} />
      )}
    </div>
  );
};

export const ChartBar: FC<IProps> = ({
  values = [],
  severalChartsValues = {},
  labels = [],
  type = "",
  title = "",
}) => {
  const { t } = useTranslation();
  const chartRef = useRef<HTMLCanvasElement>(null);
  const barValues =
    type === BAR_CHART_TYPES.quartiles
      ? severalChartsValues
      : getStaticNumberBars(values, type);
  const barLabels = getStaticNumberBars(labels, type);

  useEffect(() => {
    const myChartRef = chartRef.current;
    const options = getChartOptions(
      barValues.length,
      barLabels,
      barValues,
      title,
      type,
    );

    const chart = new Chart(myChartRef, {
      type: "bar",
      data: getChartData(barValues, type, severalChartsValues),
      options: {
        ...options,
        animation: {
          easing: "easeInOutElastic",
          onComplete: function () {
            const chartInstance = this.chart,
              ctx = chartInstance.ctx;
            ctx.font = Chart.helpers.fontString(
              Chart.defaults.global.defaultFontSize,
              Chart.defaults.global.defaultFontStyle,
              Chart.defaults.global.defaultFontFamily,
            );
            ctx.textAlign = "center";
            ctx.textBaseline = "bottom";
            this.data.datasets.forEach(function (dataset, i) {
              const meta = chartInstance.controller.getDatasetMeta(i);
              meta.data.forEach(function (bar, index) {
                const data = dataset.data[index];
                ctx.fillText(data, bar._model.x, bar._model.y - 5);
              });
            });
          },
        },
      },
    });
    return () => chart.destroy();
  }, [type, barLabels, barValues]);

  return (
    <>
      <canvas id="myChart" ref={chartRef} />
    </>
  );
};

export const ChartRadar: FC<IProps> = ({
  values = [],
  labels = [],
  type = "",
  title = "",
}) => {
  const chartRef = useRef();

  useEffect(() => {
    const truncateText = (text: string, maxLength: number) => {
      return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
    };

    const truncatedLabels = labels.map((label) =>
      typeof label === "string" ? truncateText(label, 10) : label,
    );
    const myChartRef = chartRef.current;
    const options = getChartOptions(values.length, labels, values, title, type);
    const chart = new Chart(myChartRef, {
      type: "radar",
      data: {
        labels: truncatedLabels,
        datasets: [
          {
            backgroundColor: "transparent",
            borderColor: "rgba(255,102,0,0.9)",
            fill: true,
            radius: 2,
            pointRadius: 3,
            pointBorderWidth: 1,
            pointBackgroundColor: "#495964",
            pointBorderColor: "rgba(255,102,0,0.9)",
            pointHoverRadius: 4,
            data: values,
          },
        ],
      },
      options: {
        ...options,
        barPercentage: 1,
        tooltips: {
          callbacks: {
            title: function (tooltipItem) {
              const index = tooltipItem[0].index;
              return labels[index];
            },
          },
        },
        animation: {
          easing: "easeInOutElastic",
          onComplete: function () {
            const chartInstance = this.chart,
              ctx = chartInstance.ctx;
            ctx.font = Chart.helpers.fontString(
              Chart.defaults.global.defaultFontSize,
              Chart.defaults.global.defaultFontStyle,
              Chart.defaults.global.defaultFontFamily,
            );
            ctx.textAlign = "center";
            ctx.textBaseline = "bottom";
            this.data.datasets.forEach(function (dataset, i) {
              const meta = chartInstance.controller.getDatasetMeta(i);
              meta.data.forEach(function (bar, index) {
                const data = dataset.data[index];
                let offsetX = 0;
                let offsetY = 0;

                if (index === 0) {
                  offsetX = 15;
                  offsetY = 6;
                } else if (index === 1) {
                  offsetX = 15;
                  offsetY = 10;
                } else if (index === 6) {
                  offsetX = -20;
                  offsetY = 10;
                }
                ctx.fillText(
                  data,
                  bar._model.x + offsetX,
                  bar._model.y + offsetY,
                );
              });
            });
          },
        },
      },
    });
    return () => chart.destroy();
  }, [values, labels, type]);

  return (
    <>
      <canvas id="myChart" ref={chartRef} />
    </>
  );
};

interface NewChartBarIProps {
  data: (number | null)[] | (number | null)[][];
  label: string[] | string[][];
  isLoading: boolean;
  chartContainer: string;
  size?: { width: string; height: string };
  dataContentInfoCardCar: { label: string; title: string; value: string }[];
  mainColor?: string;
  secondaryColor?: string;
  secondaryColor2?: string;
  variant?: string;
  changeVariant?: boolean;
}

export const ChartBarIvideon = ({
  data,
  label,
  isLoading,
  chartContainer,
  size,
  dataContentInfoCardCar,
  mainColor = "rgba(227, 118, 57, 1)",
  secondaryColor = "rgba(217, 220, 220, 1)",
  secondaryColor2 = "rgba(73, 90, 100, 1)",
  variant = "bar",
  changeVariant = true,
}: NewChartBarIProps) => {
  const [chartVariant, setChartVariant] = useState(variant);
  const chartRef = useRef(null);

  const handleChart = () => {
    setChartVariant((state) => (state === "bar" ? "line" : "bar"));
  };

  useEffect(() => {
    const ctx = chartRef.current.getContext("2d");
    ctx.canvas.parentNode.style.width = size?.width ?? "1000px";
    ctx.canvas.parentNode.style.height = size?.height ?? "500px";
    const arrayColor = (item, index) => {
      const color = [
        mainColor,
        chartVariant === "bar" ? secondaryColor : secondaryColor2,
      ];
      const colorList = [];
      item.map((item) => colorList.push(color[index]));
      return colorList;
    };
    const datasets = data.map((item, index) => {
      return {
        label: dataContentInfoCardCar[index]?.label,
        data: item,
        backgroundColor: arrayColor(item, index),
        borderColor: arrayColor(item, index),
        borderWidth: 1,
        fill: false,
      };
    });

    const myChart = new Chart(ctx, {
      type: chartVariant,
      data: {
        labels: label,
        datasets: datasets,
      },
      options: {
        animation: false,
        legend: {
          display: false,
        },
        tooltips: {
          backgroundColor: "rgba(255,255,255,1)",
          titleFontColor: "rgba(0,0,0,1)",
          bodyFontColor: "rgba(0,0,0,1)",
          footerFontColor: "rgba(0,0,0,1)",
          borderColor: "rgba(0, 0, 0, 0.3)",
          borderWidth: 1,
          mode: "index",
          intersect: true,
          callbacks: {
            label: function (tooltipItem, data) {
              const datasetLabel =
                data.datasets[tooltipItem.datasetIndex].label || "";
              const tooltipLabel = Number.isNaN(tooltipItem.yLabel)
                ? 0
                : tooltipItem.yLabel;
              return datasetLabel + ": " + tooltipLabel;
            },
            title: function (tooltipItems, data) {
              return label[tooltipItems[0].index];
            },
          },
        },
        title: {
          display: false,
        },
        scales: {
          yAxes: [
            {
              gridLines: {
                display: true,
              },
              ticks: {
                display: true,
                beginAtZero: true,
              },
            },
          ],
          xAxes: [
            {
              gridLines: {
                display: true,
              },
              ticks: {
                display: true,
              },
            },
          ],
        },
      },
    });

    return () => {
      myChart.destroy();
    };
  }, [data, label, isLoading, size, chartVariant]);

  return (
    <div
      className={chartContainer}
      onClick={() => {
        changeVariant && handleChart();
      }}
    >
      <canvas ref={chartRef} id="myChart" width="400" height="200"></canvas>
    </div>
  );
};
interface CircleChartIProps {
  data: number[] | any[];
  label: string[];
  isLoading: boolean;
  chartContainer: string;
  size: { width: string; height: string };
  circleColorList: string[];
}
export const CircleChart = ({
  data,
  label,
  isLoading,
  chartContainer,
  size,
  circleColorList,
}: CircleChartIProps) => {
  const chartRef = useRef(null);

  useEffect(() => {
    const ctx = chartRef.current.getContext("2d");
    ctx.canvas.parentNode.style.width = size?.width ?? "1000px";
    ctx.canvas.parentNode.style.height = size?.height ?? "500px";
    const datasets = data.map((item, index) => {
      return {
        label: label[index],
        data: item,
        backgroundColor: circleColorList,
        borderColor: circleColorList,
        borderWidth: 1,
        fill: false,
      };
    });

    const myChart = new Chart(ctx, {
      type: "doughnut",
      data: {
        labels: label,
        datasets: datasets,
      },
      options: {
        cutoutPercentage: 70,
        legend: {
          display: false,
        },
        animation: false,
        tooltips: {
          backgroundColor: "rgba(255,255,255,1)",
          titleFontColor: "rgba(0,0,0,1)",
          bodyFontColor: "rgba(0,0,0,1)",
          footerFontColor: "rgba(0,0,0,1)",
          borderColor: "rgba(0, 0, 0, 0.3)",
          borderWidth: 1,
          mode: "index",
          intersect: true,
        },
        title: {
          display: false,
        },
      },
    });
    myChart.update();
    return () => {
      myChart.destroy();
    };
  }, [data, label, isLoading, size]);

  return (
    <div className={chartContainer}>
      <canvas ref={chartRef} id="myChart" width={400} height={200}></canvas>
    </div>
  );
};

export default DealerChart;
